import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authService } from "../../services/auth.service";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Lock,
  ShieldCheck,
  ArrowRight,
  Loader2,
  Zap,
  ChevronLeft,
  Sparkles,
  KeyRound,
  CheckCircle2
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (error) setError(null);
  };

  const handleSendOtp = async () => {
    if (!formData.email) {
      setError("Please enter your email address for verification.");
      return;
    }

    setSendingOtp(true);
    setError(null);
    try {
      await authService.sendOtp(formData.email);
      setOtpSent(true);
    } catch (err: any) {
      setError(err.message || "Could not send the verification code. Please try again.");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!otpSent) {
      setError("Please verify your email address first.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(formData.password)) {
      setError("Password must be 8+ characters with uppercase, lowercase, numbers, and symbols.");
      return;
    }

    if (formData.otp.length !== 6) {
      setError("Please enter the 6-digit verification code.");
      return;
    }

    setLoading(true);

    try {
      const response = await authService.signup({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        otp: formData.otp,
      });

      localStorage.setItem("user", JSON.stringify(response.user));
      navigate("/dashboard");
    } catch (err: any) {
      setError(err.message || "Registration failed. Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex items-center justify-center p-6 font-sans relative overflow-hidden">
      {/* Soft Background Accents */}
      <div className="absolute top-0 left-0 w-[50%] h-[50%] bg-indigo-50 blur-[120px] rounded-full opacity-50"></div>
      <div className="absolute bottom-0 right-0 w-[50%] h-[50%] bg-purple-50 blur-[120px] rounded-full opacity-50"></div>

      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-16 items-center relative z-10">

        {/* Marketing Side */}
        <div className="hidden lg:flex flex-col justify-center space-y-10 pr-12">
          <div className="flex items-center gap-3">
            <img src="/vidlaya-logo.png" className="w-14 h-14 object-contain" alt="VidLaya" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">VidLaya</h1>
          </div>

          <div className="space-y-6">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-6xl font-extrabold leading-[1] tracking-tight text-slate-900"
            >
              Create videos <br />
              that <span className="text-indigo-600">stand out.</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-slate-500 font-medium text-xl max-w-md leading-relaxed"
            >
              Join VidLaya today and experience the future of professional video editing in your browser.
            </motion.p>
          </div>

          <div className="space-y-6 pt-6">
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center shrink-0">
                <Sparkles className="text-indigo-600" size={24} />
              </div>
              <div>
                <p className="font-bold text-slate-900">Fast Rendering</p>
                <p className="text-slate-500 text-sm font-medium">Cloud-based processing for instant exports.</p>
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="w-12 h-12 rounded-2xl bg-white border border-slate-100 shadow-sm flex items-center justify-center shrink-0">
                <ShieldCheck className="text-emerald-600" size={24} />
              </div>
              <div>
                <p className="font-bold text-slate-900">Secure Storage</p>
                <p className="text-slate-500 text-sm font-medium">Your assets are always safe and private.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Signup Card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="w-full max-w-lg mx-auto"
        >
          <div className="bg-white border border-slate-200 rounded-[40px] p-8 lg:p-12 shadow-xl shadow-slate-200/50">
            <div className="mb-8 text-center lg:text-left">
              <h3 className="text-2xl font-bold text-slate-900 mb-2">Create Account</h3>
              <p className="text-slate-500 font-medium text-sm">Join the community and start creating today.</p>
            </div>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-rose-50 border border-rose-100 text-rose-600 text-xs font-semibold text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 ml-1">Full Name</label>
                  <div className="relative group">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                    <Input
                      name="name"
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                      required
                      disabled={loading || sendingOtp}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 ml-1">Email Address</label>
                  <div className="flex gap-2 relative group">
                    <div className="relative flex-1 group">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors z-10" size={18} />
                      <Input
                        type="email"
                        name="email"
                        placeholder="jane@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="pl-11 h-12 w-full bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                        required
                        disabled={loading || sendingOtp || otpSent}
                      />
                      {otpSent && (
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-600 flex items-center gap-2">
                          <CheckCircle2 size={18} />
                        </div>
                      )}
                    </div>
                    {!otpSent && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={sendingOtp || !formData.email}
                        className="px-6 h-12 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-xl font-bold text-[11px] uppercase tracking-wider hover:bg-indigo-100 transition-all disabled:opacity-50 shrink-0"
                      >
                        {sendingOtp ? <Loader2 size={16} className="animate-spin" /> : "Verify"}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <AnimatePresence>
                {otpSent && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="space-y-2 overflow-hidden"
                  >
                    <label className="text-xs font-bold text-slate-700 ml-1">Verification Code</label>
                    <div className="relative group">
                      <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                      <Input
                        name="otp"
                        placeholder="000000"
                        value={formData.otp}
                        onChange={handleChange}
                        className="pl-11 h-12 bg-indigo-50/50 border-indigo-200 rounded-xl text-slate-900 placeholder-slate-300 focus:ring-indigo-500 transition-all text-center text-xl font-bold tracking-[0.4em]"
                        required
                        maxLength={6}
                        disabled={loading}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 ml-1">Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                    <Input
                      type="password"
                      name="password"
                      placeholder="********"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 ml-1">Confirm Password</label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-600 transition-colors" size={18} />
                    <Input
                      type="password"
                      name="confirmPassword"
                      placeholder="********"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-11 h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all font-medium"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>

              <div className="py-2">
                <div className="flex gap-1.5 h-1.5">
                  {[1, 2, 3, 4].map((level) => {
                    const strength = (() => {
                      let s = 0;
                      if (formData.password.length >= 8) s++;
                      if (/[A-Z]/.test(formData.password)) s++;
                      if (/[a-z]/.test(formData.password)) s++;
                      if (/[0-9]/.test(formData.password) || /[@$!%*?&]/.test(formData.password)) s++;
                      return s;
                    })();
                    return (
                      <div key={level} className={`flex-1 rounded-full bg-slate-100 overflow-hidden`}>
                        <div className={`h-full transition-all duration-500 ${level <= strength ? (strength <= 2 ? 'bg-rose-500' : strength === 3 ? 'bg-amber-500' : 'bg-emerald-500') : 'w-0'}`} style={{ width: level <= strength ? '100%' : '0%' }}></div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-4 text-[11px] text-slate-400 font-medium text-center italic">Requirements: 8+ characters with mixed case, numbers & symbols.</p>
              </div>

              <Button
                type="submit"
                disabled={loading || !otpSent}
                className="w-full h-12 bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-slate-200 disabled:text-slate-400 rounded-xl font-bold transition-all shadow-md shadow-indigo-100 active:scale-[0.98] mt-2"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <span className="flex items-center gap-2">
                    Create Account
                    <ArrowRight size={18} />
                  </span>
                )}
              </Button>

              <div className="mt-8 text-center">
                <p className="text-slate-500 text-sm font-medium">
                  Already have an account?{" "}
                  <a
                    href="/login"
                    className="text-indigo-600 font-bold hover:text-indigo-800 hover:underline transition-all ml-1"
                  >
                    Sign in
                  </a>
                </p>
              </div>
            </form>
          </div>

          {/* Bottom Link */}
          <div className="mt-8 flex justify-center">
            <a
              href="https://vidlaya.com"
              className="flex items-center gap-2 text-slate-400 hover:text-slate-600 transition-colors group text-sm font-medium"
            >
              <ChevronLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
              Back to VidLaya.com
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Signup;
